/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useState, useEffect, useCallback } from 'react';
import Clarity from '@microsoft/clarity';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

interface ConsentData {
  analytics: boolean;
  advertising: boolean;
  timestamp: number;
  version: string;
}

interface ConsentState {
  analytics: boolean;
  advertising: boolean;
  isLoaded: boolean;
}

/* -------------------------------------------------------------------------- */
/*                                   Hook                                     */
/* -------------------------------------------------------------------------- */

export default function useCookieConsent(projectId: string) {
  const [consentState, setConsentState] = useState<ConsentState>({
    analytics: false,
    advertising: false,
    isLoaded: false,
  });

  // Fonction pour détecter si l'utilisateur est dans une région nécessitant le consentement
  const isInConsentRequiredRegion = useCallback(() => {
    // Détection basique par timezone - peut être améliorée avec une API de géolocalisation
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const europeanTimezones = [
      'Europe/Paris',
      'Europe/London',
      'Europe/Berlin',
      'Europe/Rome',
      'Europe/Madrid',
      'Europe/Amsterdam',
      'Europe/Brussels',
      'Europe/Vienna',
      'Europe/Zurich',
      'Europe/Stockholm',
      'Europe/Copenhagen',
      'Europe/Oslo',
      'Europe/Helsinki',
      'Europe/Warsaw',
      'Europe/Prague',
      'Europe/Budapest',
      'Europe/Bucharest',
      'Europe/Sofia',
      'Europe/Athens',
      'Europe/Lisbon',
      'Europe/Dublin',
      'Europe/Luxembourg',
      'Europe/Malta',
      'Europe/Nicosia',
      'Europe/Riga',
      'Europe/Tallinn',
      'Europe/Vilnius',
      'Europe/Skopje',
      'Europe/Podgorica',
      'Europe/Sarajevo',
      'Europe/Zagreb',
      'Europe/Ljubljana',
      'Europe/Bratislava',
      'Europe/San_Marino',
      'Europe/Vatican',
      'Europe/Monaco',
      'Europe/Andorra',
      'Europe/Liechtenstein',
    ];

    return europeanTimezones.some((tz) => timezone.includes(tz.split('/')[1]));
  }, []);

  // Initialiser Clarity avec le mode consentement
  const initializeClarity = useCallback(() => {
    try {
      // Vérifier si nous sommes dans une région nécessitant le consentement
      const needsConsent = isInConsentRequiredRegion();

      // Initialiser Clarity normalement
      Clarity.init(projectId);

      // Si nous avons besoin de consentement, définir le consentement par défaut comme refusé
      if (needsConsent) {
        // Note: Clarity.consent() peut ne pas être disponible dans toutes les versions
        // Cette fonctionnalité sera gérée via la bannière de consentement
        console.log('Consent required for this region');
      }

      console.log('Clarity initialized with consent mode:', needsConsent);
    } catch (error) {
      console.error('Error initializing Clarity:', error);
    }
  }, [projectId, isInConsentRequiredRegion]);

  // Charger le consentement sauvegardé
  const loadSavedConsent = useCallback(() => {
    try {
      const savedConsent = localStorage.getItem('cookie-consent');
      if (savedConsent) {
        const consentData: ConsentData = JSON.parse(savedConsent);

        // Vérifier si le consentement est encore valide (13 mois selon Microsoft)
        const thirteenMonthsInMs = 13 * 30 * 24 * 60 * 60 * 1000;
        const isConsentValid =
          Date.now() - consentData.timestamp < thirteenMonthsInMs;

        if (isConsentValid) {
          setConsentState({
            analytics: consentData.analytics,
            advertising: consentData.advertising,
            isLoaded: true,
          });
        } else {
          // Consentement expiré, réinitialiser
          localStorage.removeItem('cookie-consent');
          setConsentState({
            analytics: false,
            advertising: false,
            isLoaded: true,
          });
        }
      } else {
        setConsentState({
          analytics: false,
          advertising: false,
          isLoaded: true,
        });
      }
    } catch (error) {
      console.error('Error loading saved consent:', error);
      setConsentState({
        analytics: false,
        advertising: false,
        isLoaded: true,
      });
    }
  }, []);

  // Mettre à jour le consentement dans Clarity
  const updateClarityConsent = useCallback(
    (analytics: boolean, advertising: boolean) => {
      try {
        const needsConsent = isInConsentRequiredRegion();

        if (needsConsent) {
          // Note: L'API Clarity.consent() peut varier selon la version
          // Pour l'instant, on log les préférences de consentement
          console.log('Clarity consent preferences:', {
            analytics_storage: analytics ? 'granted' : 'denied',
            ad_storage: advertising ? 'granted' : 'denied',
          });

          // TODO: Implémenter l'appel à Clarity.consent() quand l'API sera disponible
          // Clarity.consent({
          //   analytics_storage: analytics ? 'granted' : 'denied',
          //   ad_storage: advertising ? 'granted' : 'denied',
          // });
        }
      } catch (error) {
        console.error('Error updating Clarity consent:', error);
      }
    },
    [isInConsentRequiredRegion]
  );

  // Fonction pour mettre à jour le consentement
  const updateConsent = useCallback(
    (analytics: boolean, advertising: boolean) => {
      setConsentState((prev) => ({
        ...prev,
        analytics,
        advertising,
      }));

      updateClarityConsent(analytics, advertising);
    },
    [updateClarityConsent]
  );

  // Fonction pour vérifier si la bannière doit être affichée
  const shouldShowBanner = useCallback(() => {
    if (!consentState.isLoaded) return false;

    const savedConsent = localStorage.getItem('cookie-consent');
    const needsConsent = isInConsentRequiredRegion();

    return needsConsent && !savedConsent;
  }, [consentState.isLoaded, isInConsentRequiredRegion]);

  // Effet pour initialiser au montage
  useEffect(() => {
    initializeClarity();
    loadSavedConsent();
  }, [initializeClarity, loadSavedConsent]);

  // Effet pour appliquer le consentement sauvegardé à Clarity
  useEffect(() => {
    if (consentState.isLoaded) {
      updateClarityConsent(consentState.analytics, consentState.advertising);
    }
  }, [
    consentState.isLoaded,
    consentState.analytics,
    consentState.advertising,
    updateClarityConsent,
  ]);

  return {
    consentState,
    updateConsent,
    shouldShowBanner: shouldShowBanner(),
    isInConsentRequiredRegion: isInConsentRequiredRegion(),
  };
}
